import { AxiosInstance } from "axios"
import { kebab, snake } from "kitsu-core"
import camelcaseKeys from "camelcase-keys"

import { SohoApiModel, SohoModel } from "~/constants/SohoModel"
import { serialiseParams } from "~/utils/url"

import extractAxiosError, { SohoApiError } from "./extractAxiosError"
import serialize from "./serialize"

export interface RequestHeaders {
  [name: string]: string
}

export interface RequestParameters<Fields = {}, Filters = {}> {
  per_page?: number
  fields?: Fields
  filter?: Filters
  sort?: string
  include?: string | string[]
  id?: string
}

export interface BaseModel {
  id?: string
  type?: SohoModel,
  relationships?: {
    [name: string]: BaseModel | undefined
  }
}

interface SohoApiOptions {
  axios?: AxiosInstance
  headers?: RequestHeaders
  resourceCase?: ResourceCase
  timeout?: number
  errorHandler?: (args: SohoApiError) => void
}

type ResourceCase = "kebab" | "snake" | "none"

type SohoApiHeaders = RequestHeaders & JsonApiHeaders

interface JsonApiHeaders {
  Accept: "application/vnd.api+json"
  "Content-Type": "application/vnd.api+json"
}

/**
 * Creates a new `SohoApi` instance
 *
 * @param {Object} options SohoApiOptions
 * @example <caption>Using with default options</caption>
 * const api = new SohoApi()
 * @example <caption>Using another API server</caption>
 * const api = new SohoApi({
 *   baseURL: "https://api.example.org/2"
 * })
 * @example <caption>Setting headers</caption>
 * const api = new SohoApi({
 *   headers: {
 *     Authorization: "Bearer 1234567890"
 *   }
 * })
 */
class SohoApi {
  private readonly resCase = (s: string) => s

  /**
   * TODO - Use the SohoModel type instead of string - requires passing types to the API requests
   */
  private getModelUrl(model: string) {
    return SohoApiModel[model]
  }

  private readonly defaultHeaders: Partial<SohoApiHeaders> = {}
  private axios: AxiosInstance

  constructor(options: SohoApiOptions = {}) {
    const { resourceCase, errorHandler, axios, headers } = options

    if (resourceCase === "kebab") {
      this.resCase = kebab
    } else if (resourceCase !== "none") {
      this.resCase = snake
    }

    if (!axios) {
      throw new Error('An axios instance is required')
    }

    this.axios = axios

    if (headers) {
      this.defaultHeaders = headers
    }
  }

  /**
   * Get the current headers or add additional headers
   *
   * @returns {RequestHeaders} All the current headers
   * @example <caption>Get all headers</caption>
   * api.headers
   */
  get headers(): SohoApiHeaders {
    return {
      ...this.defaultHeaders,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    }
  }

  /**
   * Fetch resources
   *
   * @param {string} model Resource to fetch data from
   * @param {RequestParameters} params JSON-API request queries
   * @param {Object} headers Additional headers to send with request
   * @returns {JsonApi.ResourceObjectOrObjects} JSON-parsed response
   */
  async get<R extends JsonApi.ResourceObject, F = {}>(
    model: string,
    params: RequestParameters<R["attributes"], F> = {},
    headers: RequestHeaders = {}
  ): Promise<JsonApi.ResponseWithData<R>> {
    try {
      let [res, id, relationship] = model.split("/")

      let url = this.getModelUrl(this.resCase(res))

      if (id) {
        url = `${url}/${id}`
      }

      if (relationship) {
        url = `${url}/${this.resCase(relationship)}`
      }

      const { data } = await this.axios.get(url, {
        params,
        paramsSerializer: (p: RequestParameters) => serialiseParams(p),
        headers: { ...this.headers, ...headers },
      })

      // The deserialised does not camelCase the response.. (WTF)
      // const d = await deserialise(data)
      // TODO = We need to replace this ASAP the deserialiser should handle this
      // @ts-ignore - Need to reconsider how to get proper types out of the camelcaseKeys function
      return camelcaseKeys(data, {deep: true})
    } catch (e) {
      throw extractAxiosError(e)
    }
  }

  /**
   * Update a resource
   *
   * @param {string} type SohoModel Resource to update data in
   * @param {ResourceObject} body Data to send in the request
   * @param {RequestHeaders} headers Additional headers to send with request
   * @returns {JsonApi.ResourceObject} JSON-parsed response
   */
  async patch<R extends BaseModel>(
    type: SohoModel,
    body: Partial<R>,
    headers: RequestHeaders = {}
  ): Promise<JsonApi.ResponseWithData<R>> {
    if (typeof body === "undefined") {
      throw { message: "PATCH Resource must have a body" }
    }
    if (typeof body.id === "undefined") {
      throw { message: "PATCH Resource must have an id" }
    }

    const { relationships = {} } = body

    const url = `${this.getModelUrl(this.resCase(type))}/${body.id}`

    const relationshipFunctions = Object.keys(relationships).map(async (key: string) => {
      const relationshipUrl = `${url}/relationships/${key}`
      const relationship = relationships[key]

      if (relationship) {
        const { type, id } = relationship
        const serializedData = serialize<BaseModel>({ type, id })

        await this.axios.patch(
          relationshipUrl,
          { data: serializedData },
          { headers: { ...this.headers, ...headers } }
        )
      }
    })

    try {
      await Promise.all(relationshipFunctions)

      const serializedData = serialize<Partial<R>>({ type, ...body })
      const { data } = await this.axios.patch(
        url,
        { data: serializedData },
        { headers: { ...this.headers, ...headers } }
      )

      // @ts-ignore - Need to reconsider how to get proper types out of the camelcaseKeys function
      return camelcaseKeys(data, {deep: true})
    } catch (e) {
      throw extractAxiosError(e)
    }
  }

  /**
   * Create a new resource
   *
   * @param {string} type SohoModel Resource to create a resource under
   * @param {ResourceObject} body Data to send in the request
   * @param {RequestHeaders} headers Additional headers to send with request
   * @returns {JsonApi.ResourceObject} JSON-parsed response
   */
  async post<R extends BaseModel>(
    type: SohoModel,
    body: Partial<R>,
    headers: RequestHeaders = {}
  ): Promise<JsonApi.ResponseWithData<R>> {
    if (typeof body === "undefined") {
      throw { message: "POST Resource must have a body" }
    }

    try {
      const url = this.getModelUrl(this.resCase(type))
      const serializedData = serialize<Partial<R>>({ type, ...body })
      const { data } = await this.axios.post(
        url,
        { data: serializedData },
        { headers: { ...this.headers, ...headers } }
      )

      // @ts-ignore - Need to reconsider how to get proper types out of the camelcaseKeys function
      return camelcaseKeys(data, {deep: true})
    } catch (e) {
      throw extractAxiosError(e)
    }
  }

  /**
   * Remove a resource
   *
   * @param {string} type SohoModel to remove data from
   * @param {string} id Resource ID to remove
   * @param {RequestHeaders} headers Additional headers to send with request
   * @returns {JsonApi.ResourceObject} JSON-parsed response
   */
  async delete<R extends BaseModel>(
    type: SohoModel,
    id: string,
    headers: RequestHeaders = {}
  ): Promise<JsonApi.ResponseWithData<R>> {
    if (typeof id === "undefined") {
      throw { message: "DELETE requires an ID for the perk type" }
    }

    try {
      const url = `${this.getModelUrl(this.resCase(type))}/${id}`
      const { data } = await this.axios.delete(url, {
        headers: { ...this.headers, ...headers },
      })

      // @ts-ignore - Need to reconsider how to get proper types out of the camelcaseKeys function
      return camelcaseKeys(data, {deep: true})
    } catch (e) {
      throw extractAxiosError(e)
    }
  }
}

export default SohoApi
